import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map } from 'rxjs';
import { SalesOrderService } from './sales-order.service';
import { CustomerPaymentService } from './customer-payment.service';
import { CreditMemoService } from './credit-memo.service';
import { CustomerService } from './customer.service';
import { Customer } from '../models/customer.model';
import { addDays } from '../utils/aging.util';

export type ArPaymentStatus = 'Paid' | 'Partially Paid' | 'Unpaid';

export interface ArLedgerRow {
  id: string;
  number: string;
  invoiceNumber?: string;
  customerId: string;
  customerName: string;
  requestedDate: string;
  dueDate: string;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
  status: ArPaymentStatus;
  flagged: boolean;
}

/**
 * Unifies Sales Orders and Customer Payments into a single Accounts
 * Receivable ledger, with remaining balances derived from applied payments —
 * nothing here is stored redundantly, so the ledger can never drift out of
 * sync with the underlying orders/payments. Draft orders are excluded since
 * they don't yet represent a committed sale, and Cancelled orders are never
 * invoiced.
 */
@Injectable({ providedIn: 'root' })
export class AccountsReceivableService {
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly customerPaymentService = inject(CustomerPaymentService);
  private readonly creditMemoService = inject(CreditMemoService);
  private readonly customerService = inject(CustomerService);

  getLedger(): Observable<ArLedgerRow[]> {
    return combineLatest([
      this.salesOrderService.getAll(),
      this.customerPaymentService.getAll(),
      this.creditMemoService.getAll(),
      this.customerService.getAll(),
    ]).pipe(
      map(([salesOrders, payments, credits, customers]) => {
        const customersById = new Map<string, Customer>(customers.map((c) => [c.id, c]));
        const paidBySalesOrderId = new Map<string, number>();

        // Only successfully-processed payments settle a balance — a Failed or
        // Bounced payment stays on record but leaves the invoice outstanding.
        for (const payment of payments) {
          if (payment.status !== 'Succeeded') continue;
          for (const app of payment.applications) {
            paidBySalesOrderId.set(
              app.salesOrderId,
              (paidBySalesOrderId.get(app.salesOrderId) ?? 0) + app.amountApplied,
            );
          }
        }

        for (const credit of credits) {
          for (const app of credit.applications) {
            paidBySalesOrderId.set(
              app.salesOrderId,
              (paidBySalesOrderId.get(app.salesOrderId) ?? 0) + app.amountApplied,
            );
          }
        }

        const rows: ArLedgerRow[] = [];

        for (const order of salesOrders) {
          if (order.status === 'Draft' || order.status === 'Cancelled') continue;
          const customer = customersById.get(order.customerId);
          const paidAmount = paidBySalesOrderId.get(order.id) ?? 0;
          const remainingBalance = Math.max(0, order.total - paidAmount);
          rows.push({
            id: order.id,
            number: order.number,
            invoiceNumber: order.soInvoiceNumber,
            customerId: order.customerId,
            customerName: customer?.name ?? 'Unknown customer',
            requestedDate: order.orderDate,
            dueDate: addDays(order.orderDate, customer?.netDays ?? 0),
            totalAmount: order.total,
            paidAmount,
            remainingBalance,
            status: remainingBalance <= 0 ? 'Paid' : paidAmount > 0 ? 'Partially Paid' : 'Unpaid',
            flagged: order.flagged,
          });
        }

        return rows.sort((a, b) => b.requestedDate.localeCompare(a.requestedDate));
      }),
    );
  }
}
